<script setup>
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Avatar from 'next/avatar/Avatar.vue';
import TimeAgo from 'dashboard/components/ui/TimeAgo.vue';

const props = defineProps({
  room: { type: Object, required: true },
});

const route = useRoute();
const router = useRouter();

const isActive = computed(
  () =>
    route.name === 'conversations_through_team' &&
    Number(route.params.conversationId) === props.room.conversation_id
);

const timestamp = computed(() =>
  Math.floor(new Date(props.room.updated_at).getTime() / 1000)
);

const preview = computed(() => {
  const lastMessage = props.room.last_message;
  if (!lastMessage) return '';
  return `${lastMessage.user_name}: ${lastMessage.content}`;
});

const openRoom = event => {
  const target = router.resolve({
    name: 'conversations_through_team',
    params: {
      accountId: route.params.accountId,
      teamId: props.room.team_id,
      conversationId: props.room.conversation_id,
    },
  });

  if (event.metaKey || event.ctrlKey) {
    window.open(target.href, '_blank', 'noopener,noreferrer');
    return;
  }

  router.push(target);
};
</script>

<template>
  <button
    type="button"
    class="relative flex w-full items-start border-b border-n-slate-3 px-3 text-left hover:border-n-surface-1 hover:bg-n-alpha-1 dark:hover:bg-n-alpha-3"
    :class="{
      'bg-n-background !border-n-surface-1': isActive,
    }"
    @click="openRoom"
  >
    <div class="relative mt-4 flex-shrink-0">
      <Avatar :name="room.name" :size="32" hide-offline-status />
      <span
        class="absolute -bottom-1 -right-1 flex size-4 items-center justify-center rounded-full bg-n-brand text-white"
      >
        <span class="i-lucide-users size-2.5" />
      </span>
    </div>
    <div class="min-w-0 flex-1 py-3">
      <div class="flex items-center gap-1 px-2 pr-16">
        <h4
          class="min-w-0 flex-1 truncate text-sm font-semibold text-n-slate-12"
        >
          {{ $t('INTERNAL_CHAT.CONVERSATION_TITLE', { team: room.name }) }}
        </h4>
      </div>
      <p class="mx-2 h-6 truncate text-sm leading-6 text-n-slate-11">
        {{ preview || $t('INTERNAL_CHAT.EMPTY_MESSAGES') }}
      </p>
      <div class="absolute right-3 top-3 flex flex-col items-end gap-1">
        <TimeAgo
          :conversation-id="`internal-${room.id}`"
          :last-activity-timestamp="timestamp"
          :created-at-timestamp="timestamp"
        />
        <span
          v-tooltip="$t('INTERNAL_CHAT.ALWAYS_PINNED')"
          class="i-lucide-pin size-3.5 text-n-brand"
        />
        <span
          v-if="room.unread_count"
          class="rounded-full bg-n-brand px-1.5 text-xxs font-semibold text-white"
        >
          {{ room.unread_count }}
        </span>
      </div>
    </div>
  </button>
</template>
